// ============================================
// TOUR - Hook genérico de tour guiado (Shepherd.js)
// ============================================
// Reutilizable por cualquier módulo: no depende de ningún dominio de negocio.
// Uso: const { start } = useShepherdTour(steps, { logoUrl, name });
//      <button onClick={start}>Ver tour</button>
//      <div data-tour="mi-elemento">...</div>

import { useEffect, useRef } from 'react';
import Shepherd from 'shepherd.js';
// Gotcha crítico: sin este import el posicionamiento de los tooltips se
// rompe (quedan flotando en cualquier parte de la página, no anclados al
// elemento) — Shepherd calcula la posición vía floating-ui, que necesita
// `position: absolute` definido en este CSS base, no en el tema propio.
import 'shepherd.js/dist/css/shepherd.css';
import './shepherd-theme.css';
import type { Step, Tour } from 'shepherd.js';

export interface TourStepConfig {
  id: string;
  title: string;
  text: string;
  attachTo?: { element: string; on: 'top' | 'bottom' | 'left' | 'right' };
  /** Para steps que apuntan a elementos que cargan por un fetch (tablas, listas paginadas). */
  waitForSelector?: boolean;
  /**
   * Se ejecuta antes de mostrar este step (ej. cambiar de tab en una página
   * con navegación interna) — útil cuando el elemento a resaltar no está
   * visible hasta que se dispara una acción previa.
   */
  beforeShow?: () => void;
}

export interface TourBrandConfig {
  logoUrl: string;
  name: string;
}

const DEFAULT_CLASSES = 'ad-shepherd-theme'; // prefijo propio, evita colisión con clases de Shepherd

function addBrandHeader(brand: TourBrandConfig) {
  return function (this: Step) {
    const header = this.el?.querySelector('.shepherd-header');
    if (!header || header.querySelector('.ad-shepherd-brand')) return;

    const el = document.createElement('div');
    el.className = 'ad-shepherd-brand';
    el.innerHTML = `<img src="${brand.logoUrl}" alt="" /><span>${brand.name}</span>`;
    header.prepend(el);
  };
}

export const useShepherdTour = (steps: TourStepConfig[], brand?: TourBrandConfig) => {
  const tourRef = useRef<Tour | null>(null);

  useEffect(() => {
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: DEFAULT_CLASSES,
        scrollTo: { behavior: 'smooth', block: 'center' },
        cancelIcon: { enabled: true },
      },
    });

    steps.forEach((step, index) => {
      const isFirst = index === 0;
      const isLast = index === steps.length - 1;

      tour.addStep({
        id: step.id,
        title: step.title,
        text: step.text,
        attachTo: step.attachTo,
        beforeShowPromise: () => {
          step.beforeShow?.();
          return step.waitForSelector && step.attachTo
            ? waitForElement(step.attachTo.element)
            : Promise.resolve();
        },
        when: brand ? { show: addBrandHeader(brand) } : undefined,
        buttons: [
          ...(!isFirst
            ? [{ text: 'Atrás', action: tour.back, classes: 'ad-shepherd-btn-secondary' }]
            : []),
          {
            text: isLast ? 'Listo' : 'Siguiente',
            action: isLast ? tour.complete : tour.next,
            classes: 'ad-shepherd-btn-primary',
          },
        ],
      });
    });

    tourRef.current = tour;
    return () => { tour.complete(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps]);

  return { start: () => tourRef.current?.start() };
};

function waitForElement(selector: string, timeoutMs = 4000): Promise<void> {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) { resolve(); return; }

    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        observer.disconnect();
        resolve();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => { observer.disconnect(); resolve(); }, timeoutMs);
  });
}
